// frontend/src/components/Scenarios.js

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  Stack,
  Chip,
  LinearProgress,
  Alert,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArticleIcon from '@mui/icons-material/Article';
import MapIcon from '@mui/icons-material/Map';

// Import other necessary components
import VerticalLinearStepper from '../components/ScenariosStepper';
import ScenariosMapView from '../components/ScenariosMapView';
import SavedScenariosPanel from '../components/SavedScenariosPanel';
import SystemSelectionDialog from '../components/SystemSelectionDialog';
import ConfirmationDialog from '../components/ConfirmationDialog';
import AnalysisLogViewer from '../components/AnalysisLogViewer';
import { Link } from 'react-router-dom';

// Import your authentication utility
import { authFetch } from '../authFetch';

// Import the simulation context hook
import { useSimulation } from '../components/contexts/SimulationContext';


// API functions
const scenariosApi = {

  // Internal helper: fetch with a timeout and one automatic retry
  _fetchWithTimeout: async (url, options = {}, timeoutMs = 25000, retries = 1) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await authFetch(url, { ...options, signal: controller.signal });
        clearTimeout(timer);
        return response;
      } catch (err) {
        clearTimeout(timer);
        const isLast = attempt === retries;
        const isAbort = err.name === 'AbortError';
        if (isLast) {
          // Re-throw with a friendlier message
          throw new Error(
            isAbort
              ? 'Request timed out. The server may be busy — please try again.'
              : err.message
          );
        }
        // Wait 2 s before retry
        await new Promise(r => setTimeout(r, 2000));
      }
    }
  },

  fetchScenarios: async () => {
    try {
      const response = await authFetch('/api/scenarios/');
      return await response.json();
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      throw error;
    }
  },

  getScenario: async (scenarioId) => {
    try {
      const cacheBuster = `?v=${new Date().getTime()}`;
      const response = await authFetch(`/api/scenarios/${scenarioId}/${cacheBuster}`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching scenario ${scenarioId}:`, error);
      throw error;
    }
  },

  // NEW: cheap status-only poll
  getScenarioStatus: async (scenarioId) => {
    try {
      const response = await authFetch(`/api/scenarios/${scenarioId}/scenario_status/`);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  },

  createScenarioWithFile: async (scenarioData, files) => {
    const formData = new FormData();
    formData.append('name', scenarioData.name);
    formData.append('hazard_label', scenarioData.hazardLabel);
    if (scenarioData.selectedSystems) {
      formData.append('selected_systems', JSON.stringify(scenarioData.selectedSystems));
    }
    if (scenarioData.cascadeEnabled !== undefined) {
      formData.append('cascade_enabled', scenarioData.cascadeEnabled);
    }
    if (files && files.length > 0) {
      files.forEach(file => formData.append('hazard_file', file));
    }
    if (scenarioData.minThreshold !== '' && scenarioData.minThreshold !== null && scenarioData.minThreshold !== undefined) {
      formData.append('min_threshold', scenarioData.minThreshold);
    }
    if (scenarioData.maxThreshold !== '' && scenarioData.maxThreshold !== null && scenarioData.maxThreshold !== undefined) {
      formData.append('max_threshold', scenarioData.maxThreshold);
    }

    try {
      const response = await authFetch('/api/scenarios/', {
        method: 'POST',
        body: formData,
        headers: {},
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating scenario:', error);
      throw error;
    }
  },

  updateScenario: async (scenarioId, dataToUpdate) => {
    try {
      const response = await authFetch(`/api/scenarios/${scenarioId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToUpdate),
      });
      return await response.json();
    } catch (error) {
      console.error(`Error updating scenario ${scenarioId}:`, error);
      throw error;
    }
  },

  // Uses timeout+retry so Render's proxy idle-timeout can't kill this
  processHazard: async (scenarioId) => {
    try {
      const response = await scenariosApi._fetchWithTimeout(
        `/api/scenarios/${scenarioId}/process_hazard/`,
        { method: 'POST' },
        25000, // 25 s client timeout (Render proxy is ~30 s)
        1      // 1 retry
      );
      return await response.json();
    } catch (error) {
      console.error('Error starting hazard processing:', error);
      throw error;
    }
  },

  // Uses timeout+retry for the same reason
  runAnalysis: async (scenarioId, analysisData) => {
    try {
      const response = await scenariosApi._fetchWithTimeout(
        `/api/scenarios/${scenarioId}/run_analysis/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(analysisData),
        },
        25000,
        1
      );
      return await response.json();
    } catch (error) {
      console.error('Error running analysis:', error);
      throw error;
    }
  },

  stopAnalysis: async (scenarioId) => {
    try {
      const response = await authFetch(`/api/scenarios/${scenarioId}/stop_analysis/`, { method: 'POST' });
      return await response.json();
    } catch (error) {
      console.error('Error stopping analysis:', error);
      throw error;
    }
  },

  deleteScenario: async (scenarioId) => {
    try {
      await authFetch(`/api/scenarios/${scenarioId}/`, { method: 'DELETE' });
      return true;
    } catch (error) {
      console.error('Error deleting scenario:', error);
      throw error;
    }
  },

  getAnalysisLog: async (scenarioId) => {
    if (!scenarioId) return "";
    try {
      const cacheBuster = `?v=${new Date().getTime()}`;
      const response = await authFetch(`/api/scenarios/${scenarioId}/analysis_log/${cacheBuster}`);
      if (!response.ok) return null;
      return await response.text();
    } catch {
      return null;
    }
  },

  generateLlmSummary: async (scenarioId) => {
    try {
      const response = await authFetch(`/api/scenarios/${scenarioId}/generate_llm_summary/`, { method: 'POST' });
      return await response.json();
    } catch (error) {
      console.error('Error triggering LLM summary generation:', error);
      throw error;
    }
  },
};

export default function Scenarios() {
  const [scenarioName, setScenarioName] = useState('');
  const [hazardFiles, setHazardFiles] = useState([]);
  const [hazardLabel, setHazardLabel] = useState('');
  const [minThreshold, setMinThreshold] = useState('');
  const [maxThreshold, setMaxThreshold] = useState('');
  const [isTif, setIsTif] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [mapState, setMapState] = useState('original');
  const [step1Complete, setStep1Complete] = useState(false);
  const [hazardProcessed, setHazardProcessed] = useState(false);
  const [step2Complete, setStep2Complete] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showSavedScenarios, setShowSavedScenarios] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [showSystemSelection, setShowSystemSelection] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [analysisLog, setAnalysisLog] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [baselineConfirmOpen, setBaselineConfirmOpen] = useState(false);

  const pollingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  const isMounted = useRef(true);

  const { setIsAnalyzingGlobal, setStopAnalysisHandler } = useSimulation();

  useEffect(() => {
    setIsAnalyzingGlobal(isAnalyzing);
  }, [isAnalyzing, setIsAnalyzingGlobal]);

  const stopCurrentAnalysis = useCallback(async () => {
    if (selectedScenarioId && isAnalyzing) {
      try {
        if (isMounted.current) {
            setStatusMessage({ type: 'info', text: 'Stopping current analysis...' });
        }
        await scenariosApi.stopAnalysis(selectedScenarioId);
        if (isMounted.current) {
            setStatusMessage({ type: 'info', text: 'Analysis stopped.' });
            setIsAnalyzing(false); 
            setAnalysisProgress(0);
        }
        clearInterval(pollingIntervalRef.current);
      } catch (error) {
        if (isMounted.current) {
            setStatusMessage({ type: 'warning', text: `Could not stop analysis task: ${error.message}` });
        }
      }
    }
  }, [selectedScenarioId, isAnalyzing]);

  useEffect(() => {
    setStopAnalysisHandler(stopCurrentAnalysis);
    return () => setStopAnalysisHandler(null);
  }, [stopCurrentAnalysis, setStopAnalysisHandler]);


  const loadScenariosFromAPI = async () => {
    try {
      const scenarios = await scenariosApi.fetchScenarios();
      if (isMounted.current) {
          setSavedScenarios(scenarios);
      }
    } catch (error) {
      if (isMounted.current) {
          setStatusMessage({ type: 'error', text: `Failed to load scenarios: ${error.message}` });
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    loadScenariosFromAPI();
    return () => {
      clearInterval(pollingIntervalRef.current);
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const filesReady = hazardFiles.length > 0;
    const shapefileComplete = filesReady && (() => {
        if (!hazardFiles.some(f => f.name.toLowerCase().endsWith('.shp'))) return true;
        const exts = hazardFiles.map(f => f.name.split('.').pop().toLowerCase());
        return exts.includes('shp') && exts.includes('shx') && exts.includes('dbf') && exts.includes('prj');
    })();
    const isReady = scenarioName && hazardLabel && shapefileComplete && (!isTif || (minThreshold && maxThreshold));
    setStep1Complete(isReady);
  }, [scenarioName, hazardLabel, hazardFiles, isTif, minThreshold, maxThreshold]);

  useEffect(() => {
    if (step2Complete) setActiveStep(2);
    else if (hazardProcessed) setActiveStep(1);
    else setActiveStep(0);
  }, [hazardProcessed, step2Complete]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // --- NEW: 50MB LIMIT CHECK ---
      const MAX_SIZE_MB = 50;
      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      
      if (totalSize > MAX_SIZE_MB * 1024 * 1024) {
          setFileError(`File size exceeds the ${MAX_SIZE_MB}MB limit.`);
          setHazardFiles([]);
          if (fileInputRef.current) fileInputRef.current.value = null; // Reset input
          return;
      }
      // -----------------------------

      const extensions = files.map(f => f.name.split('.').pop().toLowerCase());
      const isShapefileUpload = extensions.some(ext => ['shp', 'shx', 'dbf', 'prj'].includes(ext));
      if (isShapefileUpload) {
        const resetAndError = (message) => { setFileError(message); setHazardFiles([]); };
        if (!extensions.includes('shp')) return resetAndError('Shapefile upload is missing the required .shp file.');
        if (!extensions.includes('shx')) return resetAndError('Shapefile upload is missing the required .shx file.');
        if (!extensions.includes('dbf')) return resetAndError('Shapefile upload is missing the required .dbf file.');
        if (!extensions.includes('prj')) return resetAndError('Shapefile upload is missing the required .prj file.');
      }
      setHazardFiles(files);
      setFileError(null);
      const primaryExtension = extensions.find(ext => ['tif', 'tiff', 'shp', 'geojson'].includes(ext));
      setIsTif(primaryExtension === 'tif' || primaryExtension === 'tiff');
    }
  };

  // UPDATED: Logic to poll immediately AND on interval
  const startPolling = (scenarioId) => {
    clearInterval(pollingIntervalRef.current);

    const LOG_FETCH_EVERY = 2; // fetch full log every N poll ticks
    let tickCount = 0;
    let consecutiveErrors = 0;
    const MAX_ERRORS = 3;

    const runPoll = async () => {
      if (!isMounted.current) {
        clearInterval(pollingIntervalRef.current);
        return;
      }

      try {
        // --- Cheap status poll on every tick ---
        const statusData = await scenariosApi.getScenarioStatus(scenarioId);

        if (!statusData) {
          // Network blip — don't surface to user yet
          consecutiveErrors++;
          if (consecutiveErrors >= MAX_ERRORS) {
            throw new Error('Lost connection to server. Please check your network.');
          }
          return;
        }
        consecutiveErrors = 0;

        if (isMounted.current) {
          setAnalysisProgress(statusData.analysis_progress || 0);
        }

        // --- Fetch full log only when needed ---
        const shouldFetchLog = (tickCount % LOG_FETCH_EVERY === 0) || currentTab === 1;
        if (shouldFetchLog) {
          const logText = await scenariosApi.getAnalysisLog(scenarioId);
          if (isMounted.current && logText !== null) {
            setAnalysisLog(logText);
          } else if (isMounted.current && logText === null) {
            setAnalysisLog(prev =>
              prev && prev !== 'Initializing analysis logs...' ? prev : 'Initializing analysis logs...'
            );
          }
        }

        tickCount++;

        // --- Status transitions (same logic as before) ---
        if (statusData.status === 'pre_complete') {
          clearInterval(pollingIntervalRef.current);
          setIsAnalyzing(false);
          setHazardProcessed(true);
          setMapState('analysis-results');
          setCurrentTab(0);
          setStatusMessage({ type: 'success', text: 'Hazard processing complete. You can now run the risk analysis.' });
          setAnalysisProgress(0);
          loadScenariosFromAPI();

        } else if (statusData.status === 'llm_pending') {
          setIsAnalyzing(true);
          setAnalysisProgress(95);
          setStatusMessage({ type: 'info', text: 'Analysis complete. Generating AI summary, please wait...' });

        } else if (statusData.status === 'completed') {
          clearInterval(pollingIntervalRef.current);
          setIsAnalyzing(false);
          setAnalysisProgress(100);
          setStatusMessage({ type: 'success', text: 'AI summary is complete. The report is ready.' });
          setStep2Complete(true);
          loadScenariosFromAPI();

        } else if (statusData.status === 'failed' || statusData.status === 'cancelled') {
          clearInterval(pollingIntervalRef.current);
          const reason = statusData.status === 'cancelled'
            ? 'Analysis was cancelled by the user.'
            : 'Analysis failed on the backend. Check the Analysis Log for details.';
          throw new Error(reason);
        }

      } catch (pollError) {
        clearInterval(pollingIntervalRef.current);
        if (isMounted.current) {
          setStatusMessage({ type: 'error', text: `Operation failed: ${pollError.message}` });
          setIsAnalyzing(false);
          setAnalysisProgress(0);
        }
      }
    };

    // Run immediately, then every 3 s
    runPoll();
    pollingIntervalRef.current = setInterval(runPoll, 3000);
  };

  const handleProcessHazard = async () => {
    if (!step1Complete) return;
    setIsAnalyzing(true);
    setStatusMessage({ type: 'info', text: 'Creating scenario and uploading file...' });
    setCurrentTab(1);
  
    try {
      const scenarioData = { name: scenarioName, hazardLabel, minThreshold, maxThreshold };
      const newScenario = await scenariosApi.createScenarioWithFile(scenarioData, hazardFiles);
      
      if (!isMounted.current) return;

      setSelectedScenarioId(newScenario.id);
      setStatusMessage({ type: 'info', text: 'File uploaded. Processing hazard area...' });
      
      await scenariosApi.processHazard(newScenario.id);
      
      startPolling(newScenario.id);

    } catch (error) {
      if (isMounted.current) {
          setStatusMessage({ type: 'error', text: `Failed to start hazard processing: ${error.message}` });
          setIsAnalyzing(false);
      }
      clearInterval(pollingIntervalRef.current);
    }
  };

  const handleRunAnalysisClick = () => {
    setShowSystemSelection(true);
  };

  const handleStartAnalysis = async (selectedSystems, cascadeEnabled) => {
    setShowSystemSelection(false);
    if (!selectedSystems || selectedSystems.length === 0) {
        setStatusMessage({ type: 'warning', text: 'No systems selected. Analysis cancelled.' });
        return;
    }
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    
    // UPDATED: Set explicit initializing text immediately
    setAnalysisLog("Initializing analysis logs...");
    setStatusMessage({ type: 'info', text: 'Running analysis...' });
    setCurrentTab(1);

    try {
      const analysisData = {
        selected_systems: selectedSystems,
        cascade_enabled: cascadeEnabled,
      };
      await scenariosApi.runAnalysis(selectedScenarioId, analysisData);
      startPolling(selectedScenarioId);
    } catch (error) {
      if (isMounted.current) {
          setStatusMessage({ type: 'error', text: `Operation failed: ${error.message}` });
          setIsAnalyzing(false);
          setAnalysisProgress(0);
      }
    }
  };
  
  const handleCreateAndRunBaseline = async () => {
    if (!scenarioName || !hazardLabel) {
        setStatusMessage({ type: 'error', text: 'Please provide a Scenario Name and a Hazard Label (e.g., "Baseline").' });
        return;
    }
    setBaselineConfirmOpen(true);
  };

  const handleConfirmBaseline = async () => {
    setBaselineConfirmOpen(false);
    setIsAnalyzing(true);
    setStatusMessage({ type: 'info', text: 'Creating baseline scenario...' });
    setCurrentTab(1);

    try {
        const scenarioData = { name: scenarioName, hazardLabel };
        const newScenario = await scenariosApi.createScenarioWithFile(scenarioData, []);

        if (!isMounted.current) return;

        setSelectedScenarioId(newScenario.id);
        setHazardProcessed(true);
        setMapState('original');
        setStatusMessage({ type: 'info', text: `Baseline scenario '${newScenario.name}' created. Please select systems to analyze.` });
        setIsAnalyzing(false);
        loadScenariosFromAPI();
        handleRunAnalysisClick();
    } catch (error) {
        if (isMounted.current) {
            setStatusMessage({ type: 'error', text: `Failed to create baseline scenario: ${error.message}` });
            setIsAnalyzing(false);
        }
    }
  };

  const loadScenario = async (scenario) => {
    const logText = await scenariosApi.getAnalysisLog(scenario.id);
    
    if (!isMounted.current) return;

    setScenarioName(scenario.name);
    setHazardLabel(scenario.hazard_label);
    setSelectedScenarioId(scenario.id);
    setHazardFiles([]);
    setFileError(null);
    setIsTif(false);

    setHazardProcessed(true); 
    setMapState(scenario.hazard_file ? 'analysis-results' : 'original');
    
    // Fallback if log is null
    setAnalysisLog(logText || "Log file not available.");
    setCurrentTab(0);

    setStep2Complete(scenario.status === 'completed');
    setShowSavedScenarios(false);
    setStatusMessage({ type: 'info', text: `Loaded scenario: ${scenario.name}` });
  };

  const deleteScenario = async (scenarioId) => {
    try {
      await scenariosApi.deleteScenario(scenarioId);
      if (isMounted.current) {
          setSavedScenarios(prev => prev.filter(s => s.id !== scenarioId));
          if (selectedScenarioId === scenarioId) clearLoadedScenario();
          setStatusMessage({ type: 'success', text: 'Scenario deleted successfully!' });
      }
    } catch (error) {
      if (isMounted.current) {
          setStatusMessage({ type: 'error', text: `Failed to delete scenario: ${error.message}` });
      }
    }
  };

  const clearLoadedScenario = () => {
    clearInterval(pollingIntervalRef.current);
    setSelectedScenarioId(null);
    setScenarioName('');
    setHazardLabel('');
    setHazardFiles([]);
    setFileError(null);
    setIsTif(false);
    setHazardProcessed(false);
    setStep2Complete(false);
    setAnalysisProgress(0);
    setIsAnalyzing(false);
    setMapState('original');
    setStatusMessage({ type: '', text: '' });
    setAnalysisLog("");
    setCurrentTab(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleConfirmReset = async () => {
    setResetConfirmOpen(false);
    
    if (selectedScenarioId && isAnalyzing) {
      await stopCurrentAnalysis();
    }
    
    clearLoadedScenario();
  };
  
  const isBaselineMode = hazardFiles.length === 0;
  let buttonText, buttonAction, isButtonDisabled;

  if (hazardProcessed) {
    buttonText = 'Run Risk Analysis';
    buttonAction = handleRunAnalysisClick;
    isButtonDisabled = isAnalyzing || step2Complete;
  } else if (isBaselineMode) {
    buttonText = 'Run Baseline Analysis';
    buttonAction = handleCreateAndRunBaseline;
    isButtonDisabled = isAnalyzing || !scenarioName || !hazardLabel || selectedScenarioId;
  } else {
    buttonText = 'Process Hazard Areas';
    buttonAction = handleProcessHazard;
    isButtonDisabled = !step1Complete || isAnalyzing || selectedScenarioId;
  }
  
  const isFormActive = selectedScenarioId || hazardFiles.length > 0 || scenarioName || hazardLabel;

  return (
    <>
      <Container maxWidth="xl" sx={{ pt: 2, pb: 2 }}>
        <Typography component="h1" variant="h5" gutterBottom>
          Scenarios
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    Configure & Run
                  </Typography>
                </Box>
                {/* 
                   CHANGE: Added disabled={isAnalyzing} here 
                */}
                <Button 
                    variant="outlined" 
                    fullWidth 
                    sx={{ mb: 2 }} 
                    startIcon={<FolderOpenIcon />} 
                    onClick={() => setShowSavedScenarios(true)}
                    disabled={isAnalyzing}
                >
                  Load Saved Scenario
                </Button>
                {selectedScenarioId && (
                    <Chip label={`Loaded: ${scenarioName}`} onDelete={() => setResetConfirmOpen(true)} color="primary" variant="outlined" size="small" sx={{ mb: 2 }} />
                )}
                <VerticalLinearStepper activeStep={activeStep} />
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <TextField label="Scenario name" variant="filled" fullWidth value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} disabled={!!selectedScenarioId} />
                  <TextField label="Hazard label" variant="filled" fullWidth value={hazardLabel} onChange={(e) => setHazardLabel(e.target.value)} disabled={!!selectedScenarioId} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Upload Hazard Data
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    disabled={!!selectedScenarioId || hazardFiles.length > 0}
                  >
                    Select File(s) (geojson or shp+shx+prj+dbf)
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        multiple
                        accept=".tif,.tiff,.geojson,.shp,.shx,.dbf,.prj,.cpg"
                        onChange={handleFileChange}
                    />
                  </Button>
                  {fileError && <Typography variant="caption" color="error">{fileError}</Typography>}
                  {hazardFiles.length > 0 && (
                    <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      {hazardFiles.map(file => (
                        <ListItem key={file.name} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}><DescriptionIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary={file.name} primaryTypographyProps={{ variant: 'caption', noWrap: true }} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {isTif && (
                    <Stack direction="row" spacing={2}>
                      <TextField label="Min Threshold" type="number" value={minThreshold} onChange={(e) => setMinThreshold(e.target.value)} variant="filled" size="small" disabled={!!selectedScenarioId} />
                      <TextField label="Max Threshold" type="number" value={maxThreshold} onChange={(e) => setMaxThreshold(e.target.value)} variant="filled" size="small" disabled={!!selectedScenarioId} />
                    </Stack>
                  )}
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Risk Analysis
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={buttonAction}
                    disabled={isButtonDisabled}
                    fullWidth
                    sx={{
                      '&.Mui-disabled': {
                        color: 'rgba(203, 202, 202, 0.79)',
                        backgroundColor: 'rgba(0, 0, 0, 0.12)'
                      },
                    }}
                  >
                    {buttonText}
                  </Button>
                  {isAnalyzing && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <LinearProgress variant={hazardProcessed ? "determinate" : "indeterminate"} value={analysisProgress} />
                      <Typography variant="caption" align="center" display="block">{analysisProgress > 0 ? `${Math.round(analysisProgress)}% Complete` : 'Processing...'}</Typography>
                    </Box>
                  )}
                  {statusMessage.text && (
                    <Alert severity={statusMessage.type || 'info'} sx={{ mt: 2 }}>{statusMessage.text}</Alert>
                  )}
                  {step2Complete && (
                    <Link to={`/report/scenarios/${selectedScenarioId}`} style={{ textDecoration: 'none' }}>
                      <Button variant="contained" color="secondary" fullWidth>Open Report</Button>
                    </Link>
                  )}
                  {isFormActive && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<RestartAltIcon />}
                      onClick={() => setResetConfirmOpen(true)}
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Reset / Start New Scenario
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8} lg={8}>
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Typography variant="h6" sx={{ px: 1, pt: 1 }}>
                  Analysis Visualization
                </Typography>
                <Tabs value={currentTab} onChange={(e, newVal) => setCurrentTab(newVal)} sx={{ px: 1, mb: 1 }}>
                  <Tab label="Map View" value={0} icon={<MapIcon />} iconPosition="start" />
                  <Tab label="Analysis Log" value={1} icon={<ArticleIcon />} iconPosition="start" disabled={!selectedScenarioId && !isAnalyzing} />
                </Tabs>
                <Box sx={{ width: "100%", height: "70vh", position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                  {currentTab === 0 && (
                     <ScenariosMapView activeState={mapState} scenarioId={selectedScenarioId} />
                  )}
                  {currentTab === 1 && (
                    <AnalysisLogViewer logContent={analysisLog} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <SavedScenariosPanel
        open={showSavedScenarios}
        onClose={() => setShowSavedScenarios(false)}
        scenarios={savedScenarios}
        onLoadScenario={loadScenario}
        onDeleteScenario={deleteScenario}
      />
      <SystemSelectionDialog
        open={showSystemSelection}
        onClose={() => setShowSystemSelection(false)}
        onConfirm={handleStartAnalysis}
      />
      <ConfirmationDialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        onConfirm={handleConfirmReset}
        title="Start a New Scenario?"
        message="Are you sure you want to reset? This will attempt to stop any analysis currently running for this scenario and clear the form."
      />
      <ConfirmationDialog
        open={baselineConfirmOpen}
        onClose={() => setBaselineConfirmOpen(false)}
        onConfirm={handleConfirmBaseline}
        title="Confirm Baseline Analysis"
        message="No hazard data loaded. Do you want to proceed with the baseline analyses?"
      />
    </>
  );
}